import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useExam, BANKING_SYLLABUS } from "@/context/ExamContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowRight, Settings, Target, Zap, BookOpen, Brain,
  FileText, BarChart3, Calendar, TrendingUp, Award, 
  Flame, ChevronRight, GraduationCap, Calculator, 
  Lightbulb, Newspaper, BookMarked, Clock, CheckCircle,
  Bot, Sparkles, Check, Landmark
} from "lucide-react";

/**
 * Banking Dashboard - Main dashboard for Banking Exam preparation
 * Following the CLARIAS-BANKING-EXECUTION-AI philosophy
 */
const BankingDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getExamConfig, getDaysToExam, switchExam } = useExam();
  const [selectedTarget, setSelectedTarget] = useState('ibps-po');
  
  const examConfig = getExamConfig();
  const daysToExam = getDaysToExam();
  const isPro = user?.banking_role === "PRO" || user?.role === "PRO";

  // Quick action cards - Core features
  const quickActions = [
    {
      title: "Learn Concepts",
      description: "Notes, formulas & solved examples",
      icon: Lightbulb,
      path: "/banking/learn",
      color: "from-blue-500 to-indigo-500",
      badge: "NEW"
    },
    {
      title: "Practice MCQs",
      description: "Bank exam-standard MCQs - Easy to Advanced",
      icon: Zap,
      path: "/banking/practice",
      color: "from-emerald-500 to-teal-500",
      badge: "FREE",
      highlight: true
    },
    {
      title: "Banking & Financial Awareness",
      description: "RBI, Banking Terms, Schemes & Economy",
      icon: Landmark,
      path: "/banking/learn-awareness",
      color: "from-amber-500 to-orange-500",
      badge: "NEW"
    }
  ];

  // Daily Execution Tasks (from Banking AI prompt)
  const dailyTasks = [
    { id: 'quant', name: '25 Quant MCQs', target: 25, done: 0, icon: Calculator },
    { id: 'reasoning', name: '20 Reasoning MCQs', target: 20, done: 0, icon: Brain },
    { id: 'english', name: '15 English MCQs', target: 15, done: 0, icon: BookOpen },
    { id: 'awareness', name: 'Banking Capsule', target: 1, done: 0, icon: Newspaper },
  ];

  // Exam target options
  const examTargets = [
    { id: 'ibps-po', name: 'IBPS PO', icon: '🏛️', color: 'bg-emerald-500' },
    { id: 'sbi-po', name: 'SBI PO', icon: '🏦', color: 'bg-blue-500' },
    { id: 'ibps-clerk', name: 'IBPS Clerk', icon: '📋', color: 'bg-violet-500' },
    { id: 'rbi-grade-b', name: 'RBI Grade B', icon: '🎯', color: 'bg-amber-500' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Top Row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🏦</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold">Banking Exams</h1>
                  <Badge className="bg-white/20 text-white border-0 text-xs">
                    {isPro ? 'PRO' : 'FREE'}
                  </Badge>
                </div>
                <p className="text-emerald-200 text-sm">IBPS PO, SBI PO, Clerk & RBI Grade B</p>
              </div>
            </div>
            
            {/* Right Side - Days & Settings */}
            <div className="flex items-center gap-3">
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="text-2xl font-bold leading-none">{daysToExam || '—'}</p>
                <p className="text-[10px] text-emerald-200">days to exam</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={switchExam}
                className="bg-white/10 hover:bg-white/20 text-white"
                title="Switch Exam"
              >
                <GraduationCap className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/account-settings")}
                className="bg-white/10 hover:bg-white/20 text-white"
              >
                <Settings className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Welcome Message */}
          <div className="mb-2">
            <h2 className="text-2xl font-bold">
              {getGreeting()}, {user?.name?.split(' ')[0] || 'Aspirant'}!
            </h2>
            <p className="text-emerald-200">Execute your daily plan. Build consistency.</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-xs">Selection Prob.</p>
                  <p className="text-2xl font-bold">—%</p>
                </div>
                <Target className="w-8 h-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-xs">Streak</p>
                  <p className="text-2xl font-bold">0 days</p>
                </div>
                <Flame className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs">MCQs Done</p>
                  <p className="text-2xl font-bold">0</p>
                </div>
                <BookOpen className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs">Mock Tests</p>
                  <p className="text-2xl font-bold">0</p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Daily Execution Engine - Core Philosophy */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Today's Execution Plan
                </h3>
                <p className="text-slate-400 text-sm">Complete these to maintain your streak</p>
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                <Clock className="w-3 h-3 mr-1" />
                ~2 hours
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {dailyTasks.map((task) => (
                <div key={task.id} className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <task.icon className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium">{task.name}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400">{task.done}/{task.target}</span>
                    <span className="text-xs text-slate-400">{Math.round((task.done/task.target)*100)}%</span>
                  </div>
                  <Progress value={(task.done/task.target)*100} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* AI Coach Card - NEW */}
        <Card className="bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-violet-500/10 border-violet-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                  CLARIAS AI Coach
                  <Sparkles className="w-4 h-4 text-violet-500" />
                </h3>
                <p className="text-slate-600 text-sm">
                  Your personal Banking exam strategist. Get daily plans, performance analysis, and smart insights.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/banking/ai-coach')}
                className="bg-gradient-to-r from-violet-500 to-purple-600 text-white"
              >
                Chat Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions.map((action) => (
            <Card
              key={action.title}
              className={`cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 border-transparent hover:border-emerald-200 ${
                action.highlight ? 'ring-2 ring-emerald-200' : ''
              }`}
              onClick={() => navigate(action.path)}
              data-testid={`quick-action-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-5">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-3`}>
                  <action.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900">{action.title}</h3>
                    <p className="text-sm text-slate-500">{action.description}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {action.badge === "NEW" && (
                      <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 text-xs animate-pulse">
                        NEW
                      </Badge>
                    )}
                    {action.badge === "FREE" ? (
                      <Badge variant="outline" className="text-xs border-emerald-200 bg-emerald-50 text-emerald-700">
                        FREE
                      </Badge>
                    ) : action.badge !== "NEW" ? (
                      <Badge variant="secondary" className="text-xs">{action.badge}</Badge>
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Exam Targets Quick Access */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-slate-900">Target Exam</h3>
                <p className="text-sm text-slate-500">Select your primary exam focus</p>
              </div>
              {selectedTarget && (
                <Badge className="bg-emerald-100 text-emerald-700">
                  {examTargets.find(e => e.id === selectedTarget)?.name} Selected
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {examTargets.map((exam) => (
                <button
                  key={exam.id}
                  onClick={() => setSelectedTarget(exam.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors border-2 ${
                    selectedTarget === exam.id 
                      ? 'bg-emerald-50 border-emerald-500' 
                      : 'bg-slate-50 hover:bg-slate-100 border-transparent hover:border-emerald-300'
                  }`}
                  data-testid={`exam-target-${exam.id}`}
                >
                  <div className={`w-12 h-12 ${exam.color} rounded-xl flex items-center justify-center relative`}>
                    <span className="text-2xl">{exam.icon}</span>
                    {selectedTarget === exam.id && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${selectedTarget === exam.id ? 'text-emerald-700' : 'text-slate-700'}`}>
                    {exam.name}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Banking Awareness Capsule - CA Sprint */}
        <Card className="bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 border-emerald-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center">
                <Newspaper className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-slate-900">Banking Awareness Capsule</h3>
                <p className="text-slate-600 text-sm">
                  Daily dose of RBI policies, banking schemes & current affairs
                </p>
                <p className="text-emerald-600 text-xs mt-1">
                  40 marks in Mains - Don't miss your daily capsule!
                </p>
              </div>
              <Button 
                onClick={() => navigate('/banking/awareness')}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
              >
                Today's Capsule <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { title: 'AI Coach', icon: Bot, path: '/banking/ai-coach', color: 'text-violet-500' },
            { title: 'Mock Tests', icon: FileText, path: '/banking/mock-test', color: 'text-emerald-500' },
            { title: 'Mains Mock', icon: BookMarked, path: '/banking/mains-mock-test', color: 'text-blue-500' },
            { title: 'Descriptive', icon: Sparkles, path: '/banking/descriptive', color: 'text-purple-500' },
            { title: 'Sectional Tests', icon: Clock, path: '/banking/sectional-test', color: 'text-teal-500' },
            { title: 'Daily Quiz', icon: Flame, path: '/banking/daily-quiz', color: 'text-orange-500' },
            { title: 'Cutoff Tracker', icon: Target, path: '/banking/cutoff-tracker', color: 'text-rose-500' },
            { title: 'Previous Papers', icon: Calendar, path: '/banking/previous-years', color: 'text-indigo-500' },
            { title: 'Analytics', icon: TrendingUp, path: '/banking/analytics', color: 'text-amber-500' },
          ].map((item) => (
            <button
              key={item.title}
              onClick={() => navigate(item.path)}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all text-left"
              data-testid={`feature-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className={`w-5 h-5 ${item.color}`} />
              <span className="font-medium text-slate-700 text-sm">{item.title}</span>
            </button>
          ))}
        </div>

        {/* Motivational Footer */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-slate-600 text-sm italic">
              "Consistency beats intensity. 60 MCQs daily, 30 days = 1800 questions. That's how toppers are made."
            </p>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

// Helper function for greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

export default BankingDashboardPage;
